// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CART_BREAKPOINT } from "@eisinc/XDL/constants";
import BadgeAvailableToShip from "@eisinc/XDL/Icons/BadgeAvailableToShip";
import BadgeOnBackorder from "@eisinc/XDL/Icons/BadgeOnBackorder";
import BadgePartialAvailability from "@eisinc/XDL/Icons/BadgePartialAvailability";
import BadgeRequiresQuote from "@eisinc/XDL/Icons/BadgeRequiresQuote";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { color, xScreen } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useMemo } from "react";
import { css } from "styled-components";

interface Props {
    isShip?: boolean;
    warehouse?: string;
    isInStock?: boolean;
    isPartial?: boolean;
    isOnBackOrder?: boolean;
    editable?: boolean;
    isCustom?: boolean;
    showQtyOrderedShipped?: boolean;
    injectedColumnsLabels?: { product?: string; total?: string };
    showReqDeliveryDate?: boolean;
    isRequiresQuote?: boolean;
}

interface GroupedLinesHeading {
    wrapper?: InjectableCss;
    descriptionStyles?: InjectableCss;
    actionHeadingWrapper?: InjectableCss;
    columnTitle?: TypographyPresentationProps;
}

const groupedLinesStyles: GroupedLinesHeading = {
    columnTitle: {
        weight: fontConfig.weight.medium,
        ...fontConfig.singleLineSmall,
        css: css`
            display: flex;
            ${IconWrapper} {
                margin-right: 8px;
            }
        `,
    },
    actionHeadingWrapper: {
        css: css`
            display: contents;
            & > div {
                position: relative;
            }
            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
    wrapper: {
        css: css`
            display: grid;
            grid-template-columns:
                92px minmax(294px, 1fr) minmax(0, 108px) minmax(92px, 98px) minmax(0, 164px) minmax(0, 136px)
                23px;
            padding: 8px;
            padding-right: 0;
            gap: 16px;
            background: ${color("common.ui2")};

            ${({ borderColor }: any) => {
                if (borderColor) {
                    return css`
                        border-top: 4px solid ${getColor(borderColor)};
                    `;
                }
                return css`
                    padding-top: 8px;
                    box-shadow: 0 -2px 0 0 ${color("common.ui5")};
                `;
            }}

            ${xScreen("lte", CART_BREAKPOINT)} {
                grid-template-columns:
                    92px minmax(120px, 1fr) minmax(0, 108px) minmax(92px, 98px) minmax(0, 164px) minmax(0, 136px)
                    23px;
            }

            ${xScreen("lte", "md")} {
                grid-template-columns: 1fr;
            }
        `,
    },

    descriptionStyles: {
        css: css`
            position: relative;
            grid-column: 1/4;

            ${({ showReqDeliveryDate, showQtyOrderedShipped }: any) => {
                if (showReqDeliveryDate || showQtyOrderedShipped) {
                    return css`
                        grid-column: 1/3;
                    `;
                }
            }}
        `,
    },
};

const GroupedLinesHeader: FC<Props> = ({
    isShip,
    warehouse,
    isInStock,
    isPartial,
    isOnBackOrder,
    editable,
    isCustom,
    showQtyOrderedShipped,
    injectedColumnsLabels,
    showReqDeliveryDate,
    isRequiresQuote,
}) => {
    const statusMap = {
        inStock: { color: "success", icon: BadgeAvailableToShip },
        partial: { color: "common.ui10", icon: BadgePartialAvailability },
        backOrder: { color: "danger", icon: BadgeOnBackorder },
        requiresQuote: { color: "info", icon: BadgeRequiresQuote },
    };

    const statusKey = isInStock
        ? "inStock"
        : isPartial
        ? "partial"
        : isOnBackOrder
        ? "backOrder"
        : isRequiresQuote
        ? "requiresQuote"
        : undefined;

    const borderColor = statusKey ? statusMap[statusKey].color : undefined;
    const iconSrc = statusKey ? statusMap[statusKey].icon : undefined;

    const styles = useMemo(() => {
        return groupedLinesStyles;
    }, [groupedLinesStyles]);

    return (
        <StyledWrapper {...styles.wrapper} {...{ borderColor }}>
            <StyledWrapper
                {...styles.descriptionStyles}
                {...{ showReqDeliveryDate, showQtyOrderedShipped }}
                className={editable ? "" : "not-editable"}
            >
                {isRequiresQuote && (
                    <Typography {...styles.columnTitle}>
                        <Icon size={16} color={borderColor} src={iconSrc} />
                        {translate("Requires Quote")}
                    </Typography>
                )}
                {isInStock && isShip && (
                    <Typography {...styles.columnTitle}>
                        <Icon size={16} color={borderColor} src={iconSrc} />
                        {translate("Available to Ship")}
                    </Typography>
                )}
                {isInStock && !isShip && (
                    <Typography {...styles.columnTitle}>
                        <Icon size={16} color={borderColor} src={iconSrc} />
                        {translate("Available to Pick Up From")}&nbsp;
                        <Typography as="strong">{warehouse}</Typography>
                    </Typography>
                )}
                {isPartial && isShip && (
                    <Typography {...styles.columnTitle}>
                        <Icon size={16} color={borderColor} src={iconSrc} />
                        {translate("Partially Available to Ship")}
                    </Typography>
                )}
                {isPartial && !isShip && (
                    <Typography {...styles.columnTitle}>
                        <Icon size={16} color={borderColor} src={iconSrc} />
                        {translate("Partially Available to Pick Up")}
                        <Typography size="16px" lineHeight="22px" as="strong">
                            {warehouse}
                        </Typography>
                    </Typography>
                )}
                {isOnBackOrder && (
                    <Typography {...styles.columnTitle}>
                        <Icon size={16} color={borderColor} src={iconSrc} />
                        {translate("On Backorder")}
                    </Typography>
                )}
                {isCustom && (
                    <Typography {...styles.columnTitle}>
                        {translate("Products Shipping from ")}
                        <Typography as="strong">{warehouse}</Typography>
                    </Typography>
                )}
                {!isInStock && !isPartial && !isOnBackOrder && !isCustom && !isRequiresQuote && (
                    <Typography {...styles.columnTitle}>
                        {injectedColumnsLabels?.product ? injectedColumnsLabels?.product : translate("Products")}
                    </Typography>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.actionHeadingWrapper}>
                {showReqDeliveryDate && (
                    <StyledWrapper>
                        <Typography {...styles.columnTitle}>{translate("Req. Delivery Date")}</Typography>
                    </StyledWrapper>
                )}
                <StyledWrapper
                    className={`${!showQtyOrderedShipped ? "qty-only" : ""} ${editable ? "" : "not-editable qty"}`}
                >
                    <Typography {...styles.columnTitle}>
                        {showQtyOrderedShipped ? translate("Qty Ordered") : translate("Qty")}
                    </Typography>
                </StyledWrapper>
                {showQtyOrderedShipped && (
                    <StyledWrapper className={editable ? "" : "not-editable"}>
                        <Typography {...styles.columnTitle}>{translate("Qty Shipped")}</Typography>
                    </StyledWrapper>
                )}
                <StyledWrapper className={!isInStock && !isPartial && !isOnBackOrder && !isCustom ? "addPadding" : ""}>
                    <Typography {...styles.columnTitle}>{translate("Price/Unit")}</Typography>
                </StyledWrapper>
                <StyledWrapper className={showQtyOrderedShipped ? "total-column" : ""}>
                    <Typography {...styles.columnTitle}>
                        {injectedColumnsLabels?.total ? injectedColumnsLabels?.total : translate("Total")}
                    </Typography>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default GroupedLinesHeader;
